import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { biensKeys } from "@/hooks/useBiens";

export interface LaunchMissionResponse {
  ok: boolean;
  launchedAt?: string;
  alreadyLaunched?: boolean;
  emailSent?: boolean;
  emailError?: string;
}

/**
 * Lancement manuel d'une mission prestataire (ELS ou PEB) — bouton « Lancer »
 * du Pipeline ELS / de la fiche bien. Appelle l'edge function `launch-mission`
 * qui pose le flag `elsMissionLanceeAt`/`pebMissionLanceeAt` ET envoie l'email
 * d'assignation immédiatement. Le `role` effectif est passé pour validation
 * (convention de l'app, cf. update_entity_with_perms).
 *
 * Au succès : invalide le cache biens pour que l'état « lancée » se reflète
 * (bouton passif + visibilité prestataire).
 */
export function useLaunchMission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { bienId: string; mission: "els" | "peb"; role: string }): Promise<LaunchMissionResponse> => {
      const { data, error } = await supabase.functions.invoke<LaunchMissionResponse>(
        "launch-mission",
        { body: { bien_id: args.bienId, mission: args.mission, role: args.role } },
      );
      if (error) throw new Error(error.message ?? "launch-mission failed");
      if (!data?.ok) throw new Error("Réponse vide ou échec du lancement");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: biensKeys.all });
    },
  });
}
